import { TRANSLATION_KEYS, type TranslationKey } from "@/config/translationKeys";
import { EN_TRANSLATIONS, NO_TRANSLATIONS } from "@/config/translations.data";
import { getDictionaryLabel, type Dictionary } from "@/domain/dictionary";
import { getLegacyArenaLabel } from "@/domain/matrix";
import type {
  AgeGroup,
  Arena,
  ColumnDefinition,
  LocalizedLabel,
  ObjectiveValue,
  View,
} from "@/domain/types";

export type { TranslationKey };

export interface TranslatorOptions {
  language: string;
  /** Admin dictionary (individual mode); `null` uses the built-in texts only. */
  dictionary: Dictionary | null;
  /** Registered arenas (individual mode); `null` uses legacy numbered arenas. */
  arenas: Arena[] | null;
}

/**
 * Resolves every user-visible text for one language. All dictionary lookups
 * go through explicit keys (`view:<id>:<code>`, `objective:<code>`, …).
 */
export interface Translator {
  language: string;
  /** Locale for dates and numbers. */
  locale: string;
  t(key: TranslationKey): string;
  /** A label without a dictionary entry (falls back to Norwegian). */
  label(label: Partial<LocalizedLabel> | undefined): string;
  viewLabel(view: View): string;
  viewValueLabel(view: View, code: string): string;
  objectiveLabel(code: string, value: ObjectiveValue): string;
  arenaLabel(arenaKey: string): string;
  /** Matrix column header for the given layout. */
  columnLabel(
    layoutId: string,
    layoutType: "ageGroups" | "categoryFromRow",
    column: ColumnDefinition,
  ): string;
  ageGroupLabel(group: AgeGroup): string;
}

function plainLabel(label: Partial<LocalizedLabel> | undefined, language: string): string {
  if (!label) return "";
  return label[language] || label.no || "";
}

/** Builds the interface text table for one language. */
function buildTextTable(
  language: string,
  dictionary: Dictionary | null,
): Record<TranslationKey, string> {
  const table = {} as Record<TranslationKey, string>;
  for (const key of TRANSLATION_KEYS) {
    const builtIn = { en: EN_TRANSLATIONS[key] || NO_TRANSLATIONS[key], no: NO_TRANSLATIONS[key] };
    table[key] = dictionary
      ? getDictionaryLabel(dictionary, "interface", key, builtIn, language)
      : language === "en"
        ? builtIn.en
        : builtIn.no;
  }
  return table;
}

export function createTranslator({ language, dictionary, arenas }: TranslatorOptions): Translator {
  const texts = buildTextTable(language, dictionary);
  const configured = (key: string, label: Partial<LocalizedLabel>) =>
    dictionary
      ? getDictionaryLabel(dictionary, "configuration", key, label, language)
      : plainLabel(label, language);

  return {
    language,
    locale: language === "en" ? "en-GB" : "nb-NO",
    t: (key) => texts[key] ?? key,
    label: (label) => plainLabel(label, language),
    viewLabel: (view) => configured(`view:${view.id}`, view.label),
    viewValueLabel: (view, code) => {
      const value = view.values[code];
      return value ? configured(`view:${view.id}:${code}`, value.label) || code : code;
    },
    objectiveLabel: (code, value) => configured(`objective:${code}`, value.label),
    arenaLabel: (arenaKey) => {
      const key = arenaKey.trim();
      const arena = arenas?.find((entry) => entry.key === key);
      if (arena) {
        return dictionary
          ? getDictionaryLabel(dictionary, "arenas", arena.key, arena.label, language)
          : plainLabel(arena.label, language);
      }
      return getLegacyArenaLabel(arenaKey, language);
    },
    columnLabel: (layoutId, layoutType, column) => {
      if (!dictionary) return plainLabel(column.label, language) || column.key;
      if (layoutType === "categoryFromRow") {
        return getDictionaryLabel(
          dictionary,
          "configuration",
          `category:${layoutId}:${column.key}`,
          column.label,
          language,
        );
      }
      return getDictionaryLabel(dictionary, "ageGroups", column.key, column.label, language);
    },
    ageGroupLabel: (group) =>
      dictionary
        ? getDictionaryLabel(dictionary, "ageGroups", group.key, group.label, language)
        : plainLabel(group.label, language),
  };
}

/** Replaces `{name}` placeholders, e.g. `format(t("syncToDbDone"), { count: 3 })`. */
export function format(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in values ? String(values[name]) : match,
  );
}
